import type { ActForOffer, ActForPeople } from '@myrewards/contracts';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { meQuery } from '../api';
import { actForPeopleQuery } from '../api/actingFor';
import { actForSwitches } from './switches';

/**
 * Who a Stock Admin may name today, read only for a Stock Admin while a switch is on and outside
 * sample-roles Preview (the read is theirs alone, and Preview is read-only). Undefined otherwise,
 * or until it's read; a page then offers only the Stock Admin's own action.
 */
export function useActForPeople(): ActForPeople | undefined {
  const { data: me } = useSuspenseQuery(meQuery);
  const switches = actForSwitches(me);
  const enabled =
    me.preview === null &&
    me.roles.some((r) => r.role === 'stock_admin') &&
    (switches.executives || switches.sectorLeads);
  const people = useQuery({ ...actForPeopleQuery, enabled }).data;
  // A disabled query keeps what it last read: once the switch is off or Preview starts, the
  // choices go at once, not when the list is next read.
  return enabled ? people : undefined;
}

/**
 * Today's Executives, to mark a budget or setting change "For an Executive" (D20): any one may be
 * named. Null when the reader can't act for Executives now.
 */
export function useExecutivesToName(): ActForOffer | null {
  const people = useActForPeople();
  if (people === undefined || !people.switches.executives) return null;
  return { role: 'executive', people: people.executives, fixed: false };
}
